import { Files, TargetSource, Vector2 } from "@app/shared";
import cn from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { CheckboxInput } from "../../../shared";
import { ColorPicker } from "../../../shared/LayoutDesigner/ColorPicker";
import { DataViewerProps } from "../DataViewer";
import { getRadarFrame, getVideoFrame } from "../ProjectWithData";
import { getTrackedTargetHistory } from "../TargetHelpers";

type CachedVideo = {
    url: string;
    content: string | null;
};
type DownloadProgress = {
    totalBytes: number;
    downloadedBytes: number;
};

type VideoLayers = {
    renderVideo: boolean;
    renderRadarPoints: boolean;
    renderRadarTargets: boolean;
    renderStaticPoints: boolean;
    renderOverlayData: boolean;
    renderTargets: boolean;
    renderTargetPath: boolean;
    renderTargetVelocity: boolean;
    renderUserTargets: boolean;

    overlayColor: string;
    radarPointColor: string;
    radarTargetColor: string;
    staticPointColor: string;
    targetPointColor: string;
    targetPathColor: string;
    targetVelocityColor: string;
    userTargetsColor: string;
}

export const VideoViewer: React.FC<DataViewerProps> = ({ setProject, onFrameChange, setLastVideoClick, setNeedsVideoClick, ...props }) => {
    const fps = props.project.videoFps ?? 20;

    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const callbackId = useRef<number | null>(null);
    const cachedVideo = useRef<CachedVideo | null>(null);

    const [downloadProgress, setDownloadProgress] = useState<DownloadProgress>();

    const [layers, setLayers] = useState<VideoLayers>({
        renderVideo: true,
        renderRadarTargets: true,
        renderRadarPoints: true,
        renderStaticPoints: true,
        renderOverlayData: true,
        renderTargets: true,
        renderTargetPath: true,
        renderTargetVelocity: true,
        renderUserTargets: true,

        overlayColor: "red",
        radarPointColor: "red",
        radarTargetColor: "orange",
        staticPointColor: "green",
        targetPointColor: "blue",
        targetPathColor: "yellow",
        targetVelocityColor: "blue",
        userTargetsColor: "purple",
    });


    const renderResults = useCallback((ctx: CanvasRenderingContext2D, videoFrameIn: number, radarFrame: number) => {
        //Do we have anything to render?
        if (!props?.project.simTargets) return;

        const projectFrame = props.project.projectFrameType === "video" ? videoFrameIn : radarFrame;
        const targets = props?.project.simTargetsByFrame?.[projectFrame] ?? [];
        const hh = props.project.pointTranslator;
        if (!hh) return;

        targets.forEach(target => {
            const targetFrame = target.source === TargetSource.Radar ? radarFrame : videoFrameIn;
            var prevPosition: Vector2 | undefined = undefined;
            //Show the target, velocity, and path
            for (var i = 0; i < target.targetHistory.length; i++) {
                const history = target.targetHistory[i];
                if (history.frameNumber > targetFrame) {
                    break;
                }

                //These have a position of 8 for the height, which is not ground coordinates, lock them on the ground with estimated height of return
                const curPosition = hh.groundToImage({ x: history.position.x, y: history.position.y, z: history.position.z });
                const curVelocity = hh.groundToImage({ x: history.velocity.x / 5 + history.position.x, y: history.velocity.y / 5 + history.position.y, z: history.position.z });

                //Draw a line connecting the previous path point to this one
                if (prevPosition && layers.renderTargetPath) {
                    ctx.strokeStyle = layers.targetPathColor;
                    ctx.lineWidth = 2;
                    ctx.beginPath();
                    ctx.moveTo(prevPosition.x, prevPosition.y);
                    ctx.lineTo(curPosition.x, curPosition.y);
                    ctx.stroke();
                }

                //If this is the current frame, render the point and veloctiy for the history
                if (history.frameNumber === targetFrame) {
                    if (layers.renderTargets) {
                        //console.log("Rendering a target current position ", history.position, " uv: ", curPosition);

                        //Draw target position
                        ctx.fillStyle = layers.targetPointColor;
                        ctx.fillRect(curPosition.x - 5, curPosition.y - 5, 10, 10);
                        ctx.fillStyle = layers.targetPointColor;
                        ctx.font = "12px Arial";
                        ctx.fillText(`#${target.radarId}`, curPosition.x + 10, curPosition.y + 5);
                    }

                    if (layers.renderTargetVelocity) {
                        ctx.strokeStyle = layers.targetVelocityColor;
                        ctx.lineWidth = 2;
                        ctx.beginPath();
                        ctx.moveTo(curPosition.x, curPosition.y);
                        ctx.lineTo(curVelocity.x, curVelocity.y);
                        ctx.stroke();
                    }
                }

                prevPosition = curPosition;

            }
        });
    }, [props.project, layers, props.project.pointTranslator]);

    const renderUserResults = useCallback((ctx: CanvasRenderingContext2D, videoFrame: number, radarFrame: number) => {
        //Do we have anything to render?
        if (!props?.project.userTargets || !layers.renderUserTargets) return;

        const projectFrame = props.project.projectFrameType === "video" ? videoFrame : radarFrame;

        const targets = props?.project.userTargetsByFrame?.[projectFrame] ?? [];
        const hh = props.project.pointTranslator;
        if (!hh) return;

        targets.forEach(target => {
            const history = getTrackedTargetHistory(target, { radar: radarFrame, video: videoFrame });
            // console.log("Rendering user target", target, history);
            if (history) {
                const curPosition = hh.groundToImage({ x: history.position.x, y: history.position.y, z: 0 });
                //Draw target position
                ctx.strokeStyle = layers.userTargetsColor;
                ctx.strokeRect(curPosition.x - 6, curPosition.y - 6, 12, 12);
            }
        });
    }, [props.project, layers]);



    const renderOverlays = useCallback((ctx: CanvasRenderingContext2D, videoFrame: number) => {
        //Do we have anything to render?
        if (!props?.project.videoOverlays || !layers.renderOverlayData) return;

        const rects = props?.project.videoOverlays[videoFrame] || [];

        //bbox [x1,y1,x2,y2]
        rects.forEach(rect => {
            const x1 = rect.bbox[0];
            const y1 = rect.bbox[1];
            const x2 = rect.bbox[2];
            const y2 = rect.bbox[3];

            ctx.strokeStyle = layers.overlayColor;
            ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);

            ctx.fillStyle = layers.overlayColor;
            ctx.font = "12px Arial";
            ctx.fillText(`Class ${rect.cla} (${rect.conf.toFixed(2)})`, x1, y1 - 2);
        });
    }, [layers, props.project]);

    const renderRadarData = useCallback((ctx: CanvasRenderingContext2D, radarFrame: number) => {
        //TODO: This should be offset based on the radar / video data start times
        if (!props?.project.radarFrames || !props.project.radarStartDate) return;

        //If we are outside of the radar frame we have nothing to render
        if (radarFrame < 0 || radarFrame > props.project.radarFrames.length) return;

        const frame = props?.project.radarFrames[radarFrame];
        const hh = props.project.pointTranslator;
        const pointSize = 4;

        if (!frame || !frame.tlvs) return;

        frame.tlvs.forEach(tlv => {
            if (layers.renderRadarPoints) {
                ctx.fillStyle = layers.radarPointColor;
                tlv.pointClouds.forEach(pc => {
                    //console.log("Rendering radar point: ", pc);
                    const point = hh ? hh.radarToImage({ x: pc.x ?? 0, y: pc.y ?? 0, z: pc.z ?? 0 }) : { x: pc.x, y: pc.y } as Vector2;
                    ctx.fillRect(point.x, point.y, pointSize, pointSize);
                });
            }

            if (layers.renderStaticPoints) {
                ctx.fillStyle = layers.staticPointColor;
                tlv.staticPointClouds.forEach(pc => {
                    const point = hh ? hh.radarToImage({ x: pc.x ?? 0, y: pc.y ?? 0, z: pc.z ?? 0 }) : { x: pc.x, y: pc.y } as Vector2;
                    ctx.fillRect(point.x, point.y, pointSize, pointSize);
                });
            }
            if (layers.renderRadarTargets) {
                tlv.radarTargets.forEach(target => {
                    const point = hh ? hh.radarToImage({ x: target.positionX ?? 0, y: target.positionY ?? 0, z: target.positionZ ?? 0 }) : { x: target.positionX, y: target.positionY } as Vector2;
                    ctx.fillStyle = layers.radarTargetColor;
                    ctx.fillRect(point.x - 5, point.y - 5, 10, 10);
                });
            }
        });
    }, [layers.renderRadarPoints, layers.renderStaticPoints, layers.renderRadarTargets, layers.radarPointColor, layers.staticPointColor, layers.radarTargetColor, props.project]);

    // Render video and rectangles on the WebGL canvas
    const renderFrame = useCallback((mediaTimeS: number) => {
        var videoFrame = getVideoFrame(props.project, mediaTimeS);

        if (!videoRef.current || !canvasRef.current) return;
        const ctx = canvasRef.current.getContext("2d");
        if (!ctx) return;
        if (layers.renderVideo) {
            ctx.drawImage(videoRef.current!, 0, 0, canvasRef.current!.width, canvasRef.current!.height);
        } else {
            ctx.clearRect(0, 0, canvasRef.current!.width, canvasRef.current!.height);
        }

        const radarFrame = getRadarFrame(props.project, mediaTimeS);

        renderOverlays(ctx, videoFrame);
        renderRadarData(ctx, radarFrame);
        renderResults(ctx, videoFrame, radarFrame);
        renderUserResults(ctx, videoFrame, radarFrame);
    }, [renderOverlays, renderRadarData, renderResults, layers.renderVideo, props.project, renderUserResults]);

    // Load the video file
    const handleVideoUrlChange = useCallback(async (url: string, abortSignal: AbortSignal) => {
        //const file = event.target.files?.[0];

        if (cachedVideo.current) {
            //If we have a current cached video, check if it is the same URL
            if (cachedVideo.current.url === url && cachedVideo.current.content) {
                if (videoRef.current) {
                    videoRef.current.src = cachedVideo.current.content;
                    renderFrame(0);
                }
                return;
            } else if (cachedVideo.current.content) {
                URL.revokeObjectURL(cachedVideo.current.content);
            }
        }
        cachedVideo.current = { url, content: null }; // We will set this to the blob once we have it

        console.log("Downloading url: " + url);

        const response = await fetch(url);
        if (!response.ok) {
            console.error("Error downloading video file: " + response.statusText);
            return;
        }

        const contentLength = response.headers.get("Content-Length");
        if (!contentLength) {
            console.error("Content-Length header is missing");
            return;
        }

        const totalBytes = parseInt(contentLength);
        let downloadedBytes = 0;

        const reader = response.body?.getReader();
        const chunks: Uint8Array[] = [];

        if (!reader) {
            console.error("Failed to get reader from response body");
            return;
        }

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            if (abortSignal.aborted) {
                console.log("Loading video aborted - setting download progress to undefined");
                setDownloadProgress(undefined);
                reader.cancel();
                cachedVideo.current = null;
                return;
            }

            if (value) {
                chunks.push(value);
                downloadedBytes += value.length;
                setDownloadProgress({ totalBytes: totalBytes, downloadedBytes: downloadedBytes });
            }
        }

        // Combine all chunks into a single Blob
        const blob = new Blob(chunks);
        const objectUrl = URL.createObjectURL(blob);

        // Cache the video URL
        cachedVideo.current = { url: url, content: objectUrl };

        if (videoRef.current) {
            videoRef.current.src = objectUrl;
            renderFrame(0);
        }

        // Reset progress after download is complete
        setDownloadProgress(undefined);
    }, [setDownloadProgress, renderFrame]);

    //Update our parent if our duration changes (this should fire when video is loaded)
    useEffect(() => {
        setProject(proj => {
            if (!proj || !proj.videoStartDate) return proj;
            const endDate = new Date(proj.videoStartDate.getTime() + (videoRef.current?.duration ?? 0) * 1000);
            if (endDate > (proj.dataEndDate ?? 0))
                proj.dataEndDate = endDate;
            return proj;
        });
    }, [videoRef.current?.duration]);

    //When the video file is changed, we need to load the new video
    useEffect(() => {
        //if (videoRef.current?.src) { return; }
        const abortController = new AbortController();

        console.log("Video URL changed", props.project?.videoFile);
        if (!props.project?.videoFile) return;

        const fetchUrl = async () => {
            const publicUrl = await Files.getPublicUrl(`visualizer/${props.project?.id}/${props.project?.videoFile}`);
            if (publicUrl) {
                await handleVideoUrlChange(publicUrl, abortController.signal);
            }
        };

        fetchUrl();
        return () => {
            //Cancel download if it is still in progress
            abortController.abort();
        }

    }, [props.project?.videoFile, handleVideoUrlChange, props.project?.id]);



    const handleNewVideoFrame = useCallback((now: number, metadata: VideoFrameCallbackMetadata) => {

        if (!videoRef.current) return;
        if (!props.isScrubbing) {
            onFrameChange?.(metadata.mediaTime);
        }
        renderFrame(metadata.mediaTime);

        callbackId.current = videoRef.current.requestVideoFrameCallback(handleNewVideoFrame);
    }, [renderFrame, onFrameChange, props.isScrubbing]);

    const handleVideoOnClick = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
        if (!videoRef.current || !props.needsVideoClick) return;
        console.log("Video clicked", event.clientX, event.clientY);

        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return;
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        setLastVideoClick?.({ x: x, y: y } as Vector2);
        setNeedsVideoClick?.(false);
    }, [props.needsVideoClick, setLastVideoClick, setNeedsVideoClick]);

    // If the user scrubs, we need to set the current time of the video
    useEffect(() => {
        //If the parent scrubs, this should adjust our current frame - otherwise it should not
        if (videoRef.current) {
            if (Math.abs(props.currentTimeS - videoRef.current.currentTime) > (1 / fps)) {
                //console.log("Scrubbing to new time", props.currentTimeS, videoRef.current.currentTime);
                videoRef.current.currentTime = props.currentTimeS;
            }
        }
    }, [props.currentTimeS, props.isScrubbing, fps]);

    //Request video frame callback from our video element
    useEffect(() => {
        //console.log("Registering handlenewVideoFrame");
        const videoRefCurrent = videoRef.current;
        if (!videoRefCurrent) return;

        if (videoRefCurrent && !callbackId.current) {
            const id = videoRefCurrent.requestVideoFrameCallback(handleNewVideoFrame);
            callbackId.current = id;
        }
        return () => {
            if (videoRefCurrent && callbackId.current !== null) {
                videoRefCurrent.cancelVideoFrameCallback(callbackId.current!);
                callbackId.current = null;
            }
        };
    }, [videoRef, handleNewVideoFrame]);

    const handleFullScreenChange = () => {
        // Make it so that our controls are absolute so we can use them over the video
    };

    useEffect(() => {
        const canvasRefCurrent = canvasRef.current;
        if (!canvasRefCurrent) return;

        canvasRefCurrent.addEventListener("fullscreenchange", handleFullScreenChange);

        return () => {
            canvasRefCurrent?.removeEventListener("fullscreenchange", handleFullScreenChange);
        };
    }, [canvasRef]);


    useEffect(() => {
        if (props.isPlaying) {
            videoRef.current?.play();
        } else {
            videoRef.current?.pause();
        }
    }, [props.isPlaying]);

    return (
        <>
            <div>
                {downloadProgress && <div className="flex flex-col justify-center items-center" style={{ width: 640, height: 480 }}>
                    <div><b>Downloading Video...</b></div>
                    <div><progress value={downloadProgress.downloadedBytes} max={downloadProgress.totalBytes} /></div>
                </div>}

                <video ref={videoRef} controls style={{ display: 'none' }} /><br />
                <canvas ref={canvasRef}
                    width={640}
                    height={480}
                    className={cn({ "border border-separators-alert": props.needsVideoClick })}
                    style={{ display: downloadProgress ? "none" : "" }}
                    onClick={handleVideoOnClick}
                />
            </div>
            <div>
                <b>Video Layers:</b>
                <div className="flex flex-row gap-3">
                    <div className="flex-grow">
                        <CheckboxInput label="Video" onChange={(e) => setLayers({ ...layers, renderVideo: e.target.checked })} checked={layers.renderVideo} />
                        <div className="flex flex-row gap-3 items-center">
                            <CheckboxInput label="Overlays" onChange={(e) => setLayers({ ...layers, renderOverlayData: e.target.checked })} checked={layers.renderOverlayData} />
                            <ColorPicker size={16} onChange={(val) => setLayers({ ...layers, overlayColor: val })} value={layers.overlayColor} />
                        </div>
                        <div className="flex flex-row gap-3 items-center">
                            <CheckboxInput label="Radar Points" onChange={(e) => setLayers({ ...layers, renderRadarPoints: e.target.checked })} checked={layers.renderRadarPoints} />
                            <ColorPicker size={16} onChange={(val) => setLayers({ ...layers, radarPointColor: val })} value={layers.radarPointColor} />

                        </div>
                        <div className="flex flex-row gap-3 items-center">
                            <CheckboxInput label="Radar Static Points" onChange={(e) => setLayers({ ...layers, renderStaticPoints: e.target.checked })} checked={layers.renderStaticPoints} />
                            <ColorPicker size={16} onChange={(val) => setLayers({ ...layers, staticPointColor: val })} value={layers.staticPointColor} />
                        </div>
                        <div className="flex flex-row gap-3 items-center">
                            <CheckboxInput label="Radar Targets" onChange={(e) => setLayers({ ...layers, renderRadarTargets: e.target.checked })} checked={layers.renderRadarTargets} />
                            <ColorPicker size={16} onChange={(val) => setLayers({ ...layers, radarTargetColor: val })} value={layers.radarTargetColor} />
                        </div>
                    </div>
                    <div className="flex-grow">
                        <div className="flex flex-row gap-3 items-center">
                            <CheckboxInput label="Sim Targets" onChange={(e) => setLayers({ ...layers, renderTargets: e.target.checked })} checked={layers.renderTargets} />
                            <ColorPicker size={16} onChange={(val) => setLayers({ ...layers, targetPointColor: val })} value={layers.targetPointColor} />
                        </div>
                        <div className="flex flex-row gap-3 items-center">
                            <CheckboxInput label="Sim Target Path" onChange={(e) => setLayers({ ...layers, renderTargetPath: e.target.checked })} checked={layers.renderTargetPath} />
                            <ColorPicker size={16} onChange={(val) => setLayers({ ...layers, targetPathColor: val })} value={layers.targetPathColor} />
                        </div>
                        <div className="flex flex-row gap-3 items-center">
                            <CheckboxInput label="Sim Target Velocity" onChange={(e) => setLayers({ ...layers, renderTargetVelocity: e.target.checked })} checked={layers.renderTargetVelocity} />
                            <ColorPicker size={16} onChange={(val) => setLayers({ ...layers, targetVelocityColor: val })} value={layers.targetVelocityColor} />
                        </div>
                        <div className="flex flex-row gap-3 items-center">
                            <CheckboxInput label="User Targets" onChange={(e) => setLayers({ ...layers, renderUserTargets: e.target.checked })} checked={layers.renderUserTargets} />
                            <ColorPicker size={16} onChange={(val) => setLayers({ ...layers, userTargetsColor: val })} value={layers.userTargetsColor} />
                        </div>
                    </div>
                </div>
            </div >

            {/* <button
                onClick={() => {
                    if (canvasRef.current) {
                        if (canvasRef.current.requestFullscreen) {
                            canvasRef.current.requestFullscreen();
                        } else if ((canvasRef.current as any).webkitRequestFullscreen) {
                            (canvasRef.current as any).webkitRequestFullscreen();
                        } else if ((canvasRef.current as any).msRequestFullscreen) {
                            (canvasRef.current as any).msRequestFullscreen();
                        }
                    }
                }}
            >
                Full Screen
            </button> */}
        </>
    );
}